import { PositionBadge } from "./PositionBadge";
import { PokerCard } from "./PokerCard";
import { fmtChips, displayChips } from "./helpers";

/**
 * ShowdownView — réplica del bloque "Showdown" del replay nativo PPPoker.
 *
 * Por cada jugador se muestra:
 *   - Nick + Ganancias totales (rojo si pierde / verde si gana)
 *   - Por cada board (en double board, 2 sub-bloques #1/#2):
 *       Posición badge | Hole cards (5) ←  →  Profit board | Board cards (5)
 *
 * Si el jugador hizo fold antes del showdown, sus hole cards se muestran como
 * dorsos pero igual se renderiza el bloque por board.
 */
function resultLabel(p) {
  const sd = p.went_to_showdown || p.showed_cards;
  const holeRevealed = (p.hole_cards || p.cards_labels || []).length > 0;
  if (sd || holeRevealed) {
    if (p.hand_label) return p.hand_label;  // backend evaluator (futuro)
    return "Mostrar";
  }
  return "Retirarse";
}

function profitTone(n) {
  if (n > 0) return "text-emerald-400";
  if (n < 0) return "text-rose-400";
  return "text-slate-400";
}

function fmtProfit(n) {
  if (n == null) return "0";
  const v = Number(n);
  if (v > 0) return `+${fmtChips(v)}`;
  return fmtChips(v);
}

export function ShowdownView({ players, board, board2, chipFactor = 1, isDoubleBoard = false, matchedUidSet }) {
  if (!players?.length) return null;

  // Sort: ganadores primero, luego mostrar, luego folders
  const sorted = [...players].sort((a, b) => {
    if (a.is_winner !== b.is_winner) return a.is_winner ? -1 : 1;
    const aSd = a.went_to_showdown || a.showed_cards || (a.hole_cards?.length || 0) > 0;
    const bSd = b.went_to_showdown || b.showed_cards || (b.hole_cards?.length || 0) > 0;
    if (aSd !== bSd) return aSd ? -1 : 1;
    return (a.seat ?? 0) - (b.seat ?? 0);
  });

  return (
    <section
      className="border-b border-white/5 px-3 py-3"
      data-testid="showdown-view"
    >
      <div className="space-y-4">
        {sorted.map((p, idx) => {
          const profitTotal = Number(displayChips(p.profit_displayed_chips ?? p.profit_total_chips ?? 0, chipFactor));
          const profitB1 = Number(displayChips(p.profit_board1_chips ?? 0, chipFactor));
          const profitB2 = Number(displayChips(p.profit_board2_chips ?? 0, chipFactor));
          const holeCards = p.hole_cards || p.cards_labels || [];
          const showed = holeCards.length > 0;
          const holeCount = showed ? holeCards.length : 6; // PLO6 fallback default

          return (
            <div
              key={p.uid || idx}
              className="space-y-2"
              data-testid={`showdown-player-${p.uid ?? idx}`}
            >
              {/* Header: nick + Ganancias totales */}
              <div className="flex items-center justify-between">
                <span className="text-[15px] font-bold text-slate-100 truncate flex items-center gap-1">
                  <span className="truncate">{p.nick || `uid:${p.uid}`}</span>
                  {matchedUidSet?.has(Number(p.uid)) && (
                    <span
                      className="text-amber-400 text-[13px] leading-none flex-shrink-0"
                      title="UID en MATCH (collusion_suspected)"
                      data-testid={`match-stars-showdown-${p.uid}`}
                    >⭐⭐</span>
                  )}
                </span>
                <div className="flex items-center gap-3 text-[13px]">
                  <span className="text-rose-400 font-medium">Ganancias totales</span>
                  <span className={`tabular-nums font-bold ${profitTone(profitTotal)}`}>
                    {fmtProfit(profitTotal)}
                  </span>
                </div>
              </div>

              {/* Board #1 row */}
              <BoardRow
                index="#1"
                position={p.position}
                holeCards={holeCards}
                holeCount={holeCount}
                showed={showed}
                resultText={p.hand_label_board1 || resultLabel(p)}
                profit={profitB1}
                board={board || []}
                bestCards={p.best_hand_cards_board1 || []}
                isDoubleBoard={isDoubleBoard}
              />

              {/* Board #2 row (solo si es double board) */}
              {isDoubleBoard && (
                <BoardRow
                  index="#2"
                  position={null /* la posición ya se mostró arriba */}
                  holeCards={holeCards}
                  holeCount={holeCount}
                  showed={showed}
                  resultText={p.hand_label_board2 || resultLabel(p)}
                  profit={profitB2}
                  board={board2 || []}
                  bestCards={p.best_hand_cards_board2 || []}
                  isDoubleBoard
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BoardRow({ index, position, holeCards, holeCount, showed, resultText, profit, board, bestCards }) {
  // Set normalizado de cartas que componen la mejor jugada (5 cartas).
  // Las cartas que NO están en este set se renderizan oscurecidas.
  // Si no hay bestCards (jugador folded o evaluador no calculó), no oscurecemos nada.
  const bestSet = (bestCards && bestCards.length) ? new Set(bestCards) : null;
  const isInBest = (label) => !bestSet || bestSet.has(label);

  return (
    <div className="flex items-start gap-2" data-testid={`showdown-board-${index}`}>
      {/* Etiqueta #1 / #2 + posición debajo */}
      <div className="w-[44px] flex flex-col items-start gap-1 pt-1.5">
        <span className="text-[12px] text-slate-400">{index}</span>
        {position && <PositionBadge position={position} />}
      </div>

      {/* Hole cards del jugador (mismas para ambos boards) */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {showed
          ? holeCards.map((c, i) => (
              <PokerCard key={i} slot={c} size={26} dim={!isInBest(c)} />
            ))
          : Array.from({ length: holeCount }).map((_, i) => (
              <PokerCard key={i} faceDown size={26} />
            ))}
      </div>

      {/* Resultado + profit del board (centrado, ocupa flex-1) */}
      <div className="flex-1 flex flex-col items-center min-w-0 px-2">
        <span className="text-[12px] text-rose-400 font-medium leading-tight">{resultText}</span>
        <span className={`text-[13px] tabular-nums font-bold ${profitTone(profit)}`}>
          {fmtProfit(profit)}
        </span>
      </div>

      {/* Board cards (5 cards) */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {(board || []).slice(0, 5).map((c, i) => (
          <PokerCard key={i} slot={c} size={26} dim={!isInBest(c)} />
        ))}
      </div>
    </div>
  );
}
